/**
 * ATTENTION: SINGLE SOURCE OF TRUTH FOR DETERMINING THE DIALOGUE ROOT NODE
 *
 * Determines the dialogue's root node by its structure.
 * The root node is an 'npc' node that is not referenced by any other node.
 * If no such node is found, returns the current rootNodeId or the first node of the dialogue.
 *
 * This function is the single source of truth for determining the root node.
 * All system components (DialogueEditor, ScenarioEditor, etc.) must use it
 * to determine the dialogue's starting point. This guarantees consistent
 * dialogue rendering across all parts of the application.
 *
 * When saving a dialogue, always explicitly set the rootNodeId obtained from this function.
 *
 * @param dialogue The dialogue for which to determine the root node
 * @returns The root node ID, or an empty string if the dialogue is empty
 */
export const determineRootNodeId = (dialogue) => {
    if (!dialogue.nodes || dialogue.nodes.length === 0) {
        return '';
    }
    // Collect all node IDs referenced in responseOptions
    const allNextNodeIds = new Set();
    dialogue.nodes.forEach(node => {
        (node.responseOptions || []).forEach(option => {
            if (option.nextDialogueId) {
                allNextNodeIds.add(option.nextDialogueId);
            }
        });
    });
    // The real root node is an 'npc' node that is not referenced by any other node
    const rootNode = dialogue.nodes.find(node => node.nodeType === 'npc' && !allNextNodeIds.has(node.id));
    if (rootNode) {
        return rootNode.id;
    }
    // If none is found, use the current rootNodeId
    else if (dialogue.rootNodeId && dialogue.nodes.find(n => n.id === dialogue.rootNodeId)) {
        return dialogue.rootNodeId;
    }
    // As a last resort, take the first node
    else if (dialogue.nodes.length > 0) {
        return dialogue.nodes[0].id;
    }
    return '';
};
/**
 * Merges data from a 'Dialogue' entity into the 'Scenario' node that corresponds to that dialogue. This is required
 * for the task of two-way synchronization between these entities.
 */
export function writeDialogueTo(dialogueOrId, scenario) {
    const hasDialogue = dialogueOrId.dialogue != null;
    const hasDialogueId = dialogueOrId.dialogueId != null;
    // Exactly one of the two must be provided as input - either the whole dialogue or its identifier.
    if (hasDialogue == hasDialogueId)
        throw new Error();
    if (hasDialogue) {
        const dialogue = dialogueOrId.dialogue;
        if (dialogue == null)
            throw new Error();
        // We have a 'Dialogue' entity at hand - we need to merge its data into the 'Scenario' entity.
        if (dialogue.levelId !== scenario.levelId)
            throw new Error();
        const scenarioNodes = scenario.nodes.filter(x => isDialogNode(x, dialogue.id));
        // No nodes corresponding to the given dialogue were found in the scenario. This is an invalid situation and
        // we should throw an exception here. But given the fragility and crookedness of the whole architecture, we ignore it.
        if (scenarioNodes.length == 0)
            return;
        if (scenarioNodes.length > 1)
            throw new Error(`Multiple nodes of the same dialogue: ${dialogue.id}.`);
        const scenarioNode = scenarioNodes[0];
        const scenarioData = scenarioNode.data;
        if (scenarioData == null)
            throw new Error(`Dialog node is missing 'data': ${dialogue.id}.`);
        const dialogueNodes = dialogue.nodes.filter(x => x.isRoot === true);
        if (dialogueNodes.length !== 1)
            throw new Error(`Multiple root nodes in the dialogue: ${dialogue.id}.`);
        const dialogueNode = dialogueNodes[0];
        scenarioData.text = dialogueNode.text;
        scenarioData.selectedNPCId = dialogueNode.selectedNPCId;
        if (Array.isArray(dialogueNode.responseOptions)) {
            const newScenarioOptions = dialogueNode.responseOptions.map(x => {
                const existingScenarioOptions = Array.isArray(scenarioData.options)
                    ? scenarioData.options.filter(o => o.id === x.id)
                    : [];
                const newScenarioOption = existingScenarioOptions.length > 0
                    ? Object.assign({}, existingScenarioOptions[0]) : { id: x.id };
                if (newScenarioOption.id !== x.id)
                    throw new Error();
                newScenarioOption.text = x.text;
                newScenarioOption.rewardLevel = x.rewardLevel;
                return newScenarioOption;
            });
            scenarioData.options = newScenarioOptions;
        }
        else {
            scenarioData.options = [];
        }
    }
    else {
        const dialogueId = dialogueOrId.dialogueId;
        if (dialogueId == null)
            throw new Error();
        // We have the identifier of a 'Dialogue' entity but not the entity itself. This means the dialogue was deleted, and
        // our synchronization task reduces to removing the deleted dialogue's nodes from the 'Scenario' entity.
        scenario.nodes = scenario.nodes.filter(x => !isDialogNode(x, dialogueId));
    }
}
/**
 * Determines whether a 'Scenario' entity node is the node corresponding to the dialogue with the given identifier.
 */
function isDialogNode(node, dialogueId) {
    // The node type must be 'npc'.
    if (node.type != 'npc')
        return false;
    const data = node.data;
    // The type stored in the data field (!) must also be 'npc'. This is not the same as what we checked above.
    if (data == null || data.type != 'npc')
        return false;
    // The 'data' object has both an id field and a dialogueId field - it's important not to confuse them here.
    return data.dialogueId === dialogueId;
}
